import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';
import {
  computeMetric,
  getSameDayLastWeek,
  toISTDateString,
} from '@/lib/periods';
import type { MetricSource } from './useMetric';
import { useOutlets } from './useOutlets';
import type { OutletRanking } from './useOutletRanking';

async function rowsOn(column: string, dates: string[]) {
  const { data, error } = await marketing
    .from('mv_outlet_kpi_daily')
    .select(`outlet_id, date, ${column}`)
    .in('date', dates);
  if (error) throw error;
  return (data ?? []) as unknown as Array<Record<string, unknown>>;
}

/**
 * Ranks outlets by yesterday vs same-day-last-week for a given metric.
 * Feeds the Today page's Top Drops / Top Spikes cards.
 */
export function useOutletDayRanking(source: MetricSource) {
  const { data: outlets = [] } = useOutlets();
  const { current, previous } = getSameDayLastWeek();
  const column = source;

  return useQuery<{ rankings: OutletRanking[] }>({
    enabled: outlets.length > 0,
    queryKey: ['outlet-day-ranking', source, current.start.toISOString()],
    queryFn: async () => {
      const yesterday = toISTDateString(current.start);
      const lastWeek = toISTDateString(previous.start);
      const rows = await rowsOn(column, [yesterday, lastWeek]);

      const byDateOutlet = new Map<string, number>();
      for (const r of rows) {
        const k = `${r.date}::${r.outlet_id}`;
        byDateOutlet.set(k, (byDateOutlet.get(k) ?? 0) + Number(r[column] ?? 0));
      }

      const rankings: OutletRanking[] = outlets.map((o) => {
        const cur = byDateOutlet.get(`${yesterday}::${o.id}`) ?? 0;
        const prev = byDateOutlet.get(`${lastWeek}::${o.id}`) ?? 0;
        return {
          outletId: o.id,
          outletName: o.name,
          city: o.city,
          metric: computeMetric(cur, prev),
          trend: [prev, cur],
        };
      });

      return { rankings };
    },
    staleTime: 60_000,
  });
}
